import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Users,
  Network,
  TrendingUp,
  Briefcase,
  ShieldCheck,
  MapPin,
  Hash,
  Mail,
  Phone,
  Layers,
  ArrowLeft,
  Edit3,
  Sliders,
  DollarSign,
  PieChart,
  CheckCircle2,
  Sparkles,
  AlertTriangle,
  ShieldAlert,
  Landmark,
  Award,
  Wallet,
  ClipboardList,
  Banknote
} from "lucide-react";

import { getSocieteById } from "../../../features/familles/api/societeAPI";
import PageAnimator from "../../../components/ui/PageAnimator";
import MiniLoader from "../../../components/MiniLoader";
import Breadcrumbs from "../../../components/ui/Breadcrumbs";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/Badge";
import AuditField from "../../../features/familles/components/details/AuditField";
import SocieteAudit from "../../../features/familles/components/SocieteAudit";
import { useNotification } from "../../../context/NotificationContext";

export default function DetailSocietes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useNotification();

  const [societe, setSociete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("admin");
  const [tabLoading, setTabLoading] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);

  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;
    setTabLoading(true);
    setActiveTab(newTab);
    setTimeout(() => {
      setTabLoading(false);
    }, 120);
  };

  const fetchSociete = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSocieteById(id);
      setSociete(data);
    } catch (err) {
      console.error("Erreur chargement société :", err);
      showToast("Impossible de charger les détails de la société.", "error");
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    fetchSociete();
  }, [fetchSociete]);

  const formatEuro = (val) => {
    if (val == null || isNaN(val)) return "—";
    return `${Number(val).toLocaleString("fr-FR")} €`;
  };

  const formatEuroCompact = (val) => {
    if (!val || val === 0) return "—";
    if (val >= 1000000) return `${(val / 1000000).toFixed(2)} M€`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)} k€`;
    return `${val} €`;
  };

  const formatSiren = (siren) => {
    if (!siren) return "N/A";
    const clean = String(siren).replace(/\s/g, "");
    if (clean.length === 9) {
      return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6, 9)}`;
    }
    return siren;
  };

  const handleNavigateAllocation = () => {
    navigate("/outils/allocation", {
      state: {
        clientId: societe?.id,
        clientName: societe?.denomination || societe?.raisonSociale,
        clientType: "societe",
        valorisation: societe?.valorisationEstimee ?? 0,
        tresorerie: societe?.tresorerieDisponible ?? 0,
        placements: societe?.placementsEntreprise ?? 0,
        dettes: societe?.dettes ?? 0
      }
    });
  };

  const handleNavigateImpots = () => {
    navigate("/outils/impots", {
      state: {
        clientId: societe?.id,
        clientName: societe?.denomination || societe?.raisonSociale,
        clientType: "societe",
        remunerationDirigeant: societe?.gerant?.remuneration ?? 0,
        dividendes: societe?.gerant?.dividendes ?? 0,
        chiffreAffaires: societe?.chiffreAffaires ?? 0,
        resultatNet: societe?.resultatNet ?? 0,
        formeJuridique: societe?.formeJuridique,
        isHolding: societe?.holding
      }
    });
  };

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-12 bg-workspace">
        <MiniLoader />
      </div>
    );
  }

  if (!societe) {
    return (
      <div className="p-8 text-center bg-workspace flex flex-col items-center justify-center space-y-4">
        <Building2 className="w-16 h-16 text-subtle" />
        <h2 className="text-xl font-bold text-primary">Société introuvable</h2>
        <Button variant="outline" onClick={() => navigate("/prospects/societes")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour à la liste des sociétés
        </Button>
      </div>
    );
  }

  const gerant = societe.gerant || null;
  const hasGerant = Boolean(gerant && (gerant.nom || gerant.prenom));

  const actionnaires = societe.actionnaires || [];
  const beneficiairesEffectifs = societe.beneficiairesEffectifs || [];
  const filiales = societe.filiales || [];

  const tabs = [
    { id: "admin", label: "Identité & Légal", icon: Building2 },
    { id: "gerance", label: "Gouvernance & Gérant", icon: Users },
    { id: "actionnariat", label: "Actionnariat & RBE", icon: PieChart },
    { id: "financier", label: "Santé Financière", icon: TrendingUp },
    { id: "organigramme", label: "Cartographie & Liens", icon: Network },
    { id: "audit", label: "Audit & Stratégie", icon: Sparkles },
  ];

  // Ratios financiers calculés avec protection division par zéro / null
  const margeEbitda = (societe.chiffreAffaires > 0 && societe.ebitda != null)
    ? ((societe.ebitda / societe.chiffreAffaires) * 100).toFixed(1)
    : "—";
  const margeNette = (societe.chiffreAffaires > 0 && societe.resultatNet != null)
    ? ((societe.resultatNet / societe.chiffreAffaires) * 100).toFixed(1)
    : "—";
  const levierDette = (societe.ebitda > 0 && societe.dettes != null)
    ? (societe.dettes / societe.ebitda).toFixed(1)
    : "—";
  const tresorerieNette = (societe.tresorerieDisponible != null || societe.dettes != null)
    ? (societe.tresorerieDisponible || 0) - (societe.dettes || 0)
    : null;

  return (
    <PageAnimator>
      <div className="flex h-full w-full flex-col overflow-y-auto bg-workspace p-4 sm:p-8 space-y-6">
        
        {/* Fil d'Ariane & Actions d'en-tête */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <Breadcrumbs
            items={[
              { label: "Sociétés", path: "/prospects/societes" },
              { label: societe.denomination || societe.raisonSociale || "Fiche Société" }
            ]}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="accent"
              size="sm"
              onClick={() => setShowAuditModal(true)}
              className="flex items-center gap-1.5 shadow-hp-sm text-xs sm:text-sm"
            >
              <ClipboardList className="w-4 h-4" />
              Audit Patrimonial
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleNavigateAllocation}
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <Sliders className="w-4 h-4" />
              Allocation
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleNavigateImpots}
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <Banknote className="w-4 h-4" />
              Imposition
            </Button>
          </div>
        </div>

        {/* Header du Dossier Société (Style DetailPage) */}
        <div className="workspace-card p-5 sm:p-6 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="w-13 h-13 rounded-2xl bg-gradient-to-br from-[var(--hp-ink-800)] to-[var(--hp-ink-950)] text-white font-extrabold text-xl flex items-center justify-center shadow-hp-md shrink-0 border border-white/10 ring-2 ring-workspace"
              >
                {societe.denomination?.slice(0, 2).toUpperCase() || <Building2 className="w-6 h-6" />}
              </motion.div>

              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-primary">
                    {societe.denomination || societe.raisonSociale}
                  </h1>
                  <Badge variant="info">{societe.statut || "Client Actif"}</Badge>
                  {societe.formeJuridique && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-secondary/15 text-secondary border border-secondary/20">
                      {societe.formeJuridique}
                    </span>
                  )}
                  {societe.holding && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-secondary/10 text-secondary border border-secondary/20">
                      <Layers className="w-3 h-3" /> Holding
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2.5 mt-1 text-xs text-subtle">
                  <span className="font-mono font-medium text-muted">
                    SIREN : {formatSiren(societe.siren)}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-secondary" />
                    {societe.ville ? `${societe.ville}${societe.codePostal ? ` (${societe.codePostal})` : ""}` : "Siège non renseigné"}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5 text-secondary" />
                    {societe.secteurActivite || "Secteur non renseigné"}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1 font-semibold text-secondary">
                    <Users className="w-3.5 h-3.5" />
                    Gérance : {hasGerant ? `${gerant.prenom} ${gerant.nom}` : "Non déclarée"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAuditModal(true)}
                className="flex items-center gap-1.5 text-xs text-subtle hover:text-primary rounded-[var(--hp-radius-md)] transition-colors"
              >
                <Edit3 className="w-4 h-4 text-secondary" />
                <span>Modifier la fiche</span>
              </Button>
            </div>
          </div>

          {/* Barre de Navigation par Onglets avec indicateur Framer Motion */}
          <nav className="border-b border-workspace w-full">
            <div className="-mb-px flex space-x-1 sm:space-x-2 overflow-x-auto pb-1 scrollbar-none">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`relative flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold rounded-t-lg transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-[var(--hp-color-accent-text)] bg-surface-muted/50"
                        : "text-subtle hover:text-primary hover:bg-surface-muted/30"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{tab.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeTabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--hp-color-accent)]"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* 4 CARTES KPIS FINANCIERS HAUT DE PAGE */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 rounded-2xl border border-workspace bg-surface shadow-hp-xs">
            <div className="flex items-center justify-between text-subtle text-xs font-semibold uppercase tracking-wider">
              <span>Chiffre d'Affaires</span>
              <TrendingUp className="w-4 h-4 text-primary" />
            </div>
            <p className="text-2xl font-black text-primary mt-2">
              {formatEuroCompact(societe.chiffreAffaires)}
            </p>
            <p className="text-[11px] text-subtle mt-1">Dernier exercice clôturé</p>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 rounded-2xl border border-workspace bg-surface shadow-hp-xs">
            <div className="flex items-center justify-between text-subtle text-xs font-semibold uppercase tracking-wider">
              <span>EBITDA</span>
              <Award className="w-4 h-4 text-secondary" />
            </div>
            <p className="text-2xl font-black text-secondary mt-2">
              {formatEuroCompact(societe.ebitda)}
            </p>
            <p className="text-[11px] text-subtle mt-1">
              Marge d'EBITDA : <strong className="text-primary">{margeEbitda}%</strong>
            </p>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 rounded-2xl border border-workspace bg-surface shadow-hp-xs">
            <div className="flex items-center justify-between text-subtle text-xs font-semibold uppercase tracking-wider">
              <span>Résultat Net</span>
              <DollarSign className="w-4 h-4 text-emerald-600" />
            </div>
            <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-2">
              {formatEuroCompact(societe.resultatNet)}
            </p>
            <p className="text-[11px] text-subtle mt-1">
              Marge Nette : <strong className="text-primary">{margeNette}%</strong>
            </p>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 rounded-2xl border border-workspace bg-surface shadow-hp-xs">
            <div className="flex items-center justify-between text-subtle text-xs font-semibold uppercase tracking-wider">
              <span>Valorisation Estimée</span>
              <Landmark className="w-4 h-4 text-primary" />
            </div>
            <p className="text-2xl font-black text-primary mt-2">
              {formatEuroCompact(societe.valorisationEstimee)}
            </p>
            <p className="text-[11px] text-subtle mt-1">
              Multiple VE / EBITDA : <strong className="text-primary">
                {societe.ebitda > 0 ? (societe.valorisationEstimee / societe.ebitda).toFixed(1) + "x" : "—"}
              </strong>
            </p>
          </motion.div>
        </div>

        {/* CONTENU DES ONGLETS AVEC ANIMATION ET MINILOADER */}
        <AnimatePresence mode="wait">
          {tabLoading ? (
            <motion.div
              key="tab-loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="workspace-card p-16 flex flex-col justify-center items-center min-h-[300px] space-y-3"
            >
              <MiniLoader />
              <p className="text-xs text-subtle font-medium">Chargement de la section...</p>
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >

          {/* ========================================================= */}
          {/* TAB 1 : IDENTITÉ & LÉGAL                                  */}
          {/* ========================================================= */}
          {activeTab === "admin" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bloc 1 : Données Légales et Juridiques */}
              <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                <div className="flex items-center gap-3 border-b border-workspace pb-3">
                  <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-primary">Identité Légale & Immatriculation</h3>
                    <p className="text-xs text-subtle">Informations juridiques certifiées</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <AuditField label="Dénomination Sociale" value={societe.denomination || societe.raisonSociale || "—"} />
                  <AuditField label="Forme Juridique" value={societe.formeJuridique || "—"} />
                  <AuditField label="Numéro SIREN" value={formatSiren(societe.siren)} />
                  <AuditField label="Numéro SIRET" value={societe.siret || "—"} />
                  <AuditField label="N° TVA Intracommunautaire" value={societe.numeroTva || "—"} />
                  <AuditField label="Code NAF / APE" value={societe.codeNaf || "—"} />
                  <AuditField label="Date d'immatriculation" value={societe.dateCreation ? new Date(societe.dateCreation).toLocaleDateString("fr-FR") : "—"} />
                  <AuditField label="Régime d'imposition" value={societe.formeJuridique === "SCI" ? "Translucide (IR / IS sur option)" : (societe.formeJuridique ? "Impôt sur les Sociétés (IS)" : "—")} />
                </div>
              </div>

              {/* Bloc 2 : Capital, Siège & Contact */}
              <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                <div className="flex items-center gap-3 border-b border-workspace pb-3">
                  <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-primary">Siège Social & Capital Social</h3>
                    <p className="text-xs text-subtle">Coordonnées et structure financière</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <AuditField label="Capital Social" value={formatEuro(societe.capitalSocial)} />
                  <AuditField label="Effectif Salarié" value={societe.effectif != null ? `${societe.effectif} salarié(s)` : "—"} />
                  <AuditField label="Adresse du Siège" value={societe.adresseSiege || societe.adresse || "—"} />
                  <AuditField label="Ville & Code Postal" value={societe.ville ? `${societe.ville}${societe.codePostal ? ` (${societe.codePostal})` : ""}` : "—"} />
                  <AuditField label="Pays" value={societe.pays || "—"} />
                  <AuditField label="Holding Familiale" value={societe.holding === true ? "Oui (Structure animatrice)" : (societe.holding === false ? "Non (Société d'exploitation)" : "Non renseigné")} />
                  <AuditField label="Email Officiel" value={societe.email || "—"} />
                  <AuditField label="Site Web" value={societe.siteWeb || "—"} />
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 2 : GOUVERNANCE & GÉRANT                              */}
          {/* ========================================================= */}
          {activeTab === "gerance" && (
            <div className="space-y-6">
              {!hasGerant ? (
                <div className="workspace-card p-12 text-center flex flex-col items-center justify-center space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-surface-muted flex items-center justify-center text-subtle">
                    <Users className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-bold text-primary">Dirigeant non renseigné</h3>
                  <p className="text-xs text-subtle max-w-sm">
                    Aucun dirigeant ou mandataire social n'a été déclaré pour cette personne morale.
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setShowAuditModal(true)} className="text-xs mt-1">
                    Renseigner la gouvernance
                  </Button>
                </div>
              ) : (
                <div className="workspace-card p-6 sm:p-8 rounded-2xl border border-workspace bg-surface shadow-hp-sm">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-b border-workspace pb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-secondary/10 border-2 border-secondary text-secondary flex items-center justify-center font-black text-xl">
                        {`${gerant.prenom?.charAt(0) || ""}${gerant.nom?.charAt(0) || "D"}`.toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-xl font-black text-primary">
                            {gerant.prenom} {gerant.nom}
                          </h3>
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-secondary text-white">
                            {gerant.role || "Mandataire"}
                          </span>
                          {gerant.statutSocial && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-surface-muted text-subtle border border-workspace">
                              {gerant.statutSocial}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-subtle mt-1 flex items-center gap-3">
                          {gerant.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5 text-secondary" /> {gerant.email}
                            </span>
                          )}
                          {gerant.email && gerant.telephone && <span>•</span>}
                          {gerant.telephone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5 text-secondary" /> {gerant.telephone}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 bg-surface-muted/50 p-4 rounded-xl border border-workspace">
                      <div className="text-center px-3">
                        <span className="text-[10px] uppercase font-bold text-subtle block">Détention Capital</span>
                        <span className="text-lg font-black text-secondary">
                          {gerant.detentionCapital != null ? `${gerant.detentionCapital}%` : "—"}
                        </span>
                      </div>
                      <div className="h-8 w-px bg-workspace" />
                      <div className="text-center px-3">
                        <span className="text-[10px] uppercase font-bold text-subtle block">Droits de Vote</span>
                        <span className="text-lg font-black text-primary">
                          {gerant.detentionVotes != null ? `${gerant.detentionVotes}%` : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Métriques Rémunération & Patrimoine Dirigeant */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6">
                    <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace space-y-1">
                      <span className="text-[11px] font-semibold text-subtle uppercase tracking-wider block">
                        Rémunération Annuelle (Art. 62 / Salaire)
                      </span>
                      <span className="text-xl font-black text-primary block">
                        {formatEuro(gerant.remuneration)}
                      </span>
                      <span className="text-[10px] text-subtle block">Reportable en simulation fiscale</span>
                    </div>

                    <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace space-y-1">
                      <span className="text-[11px] font-semibold text-subtle uppercase tracking-wider block">
                        Dividendes Annuels Perçus
                      </span>
                      <span className="text-xl font-black text-secondary block">
                        {formatEuro(gerant.dividendes)}
                      </span>
                      <span className="text-[10px] text-subtle block">Soumis au PFU (Flat Tax 30%)</span>
                    </div>

                    <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace space-y-1">
                      <span className="text-[11px] font-semibold text-subtle uppercase tracking-wider block">
                        Compte Courant d'Associé (CCA)
                      </span>
                      <span className="text-xl font-black text-emerald-600 dark:text-emerald-400 block">
                        {formatEuro(gerant.compteCourantAssocie)}
                      </span>
                      <span className="text-[10px] text-subtle block">Créance remboursable sans fiscalité</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 3 : ACTIONNARIAT & RBE (BÉNÉFICIAIRES EFFECTIFS)      */}
          {/* ========================================================= */}
          {activeTab === "actionnariat" && (
            <div className="space-y-6">
              {/* TABLEAU DE CAPITALISATION (CAP TABLE) */}
              <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                <div className="flex items-center justify-between border-b border-workspace pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
                      <PieChart className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-primary">Tableau de Capitalisation (Cap Table)</h3>
                      <p className="text-xs text-subtle">Répartition du capital et des droits de vote entre associés</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-subtle">
                    {actionnaires.length} Actionnaire(s)
                  </span>
                </div>

                {/* Barre visuelle de répartition */}
                <div className="h-3 w-full rounded-full bg-surface-muted overflow-hidden flex shadow-inner">
                  {actionnaires.map((act, i) => {
                    const colors = ["bg-secondary", "bg-primary", "bg-emerald-500", "bg-amber-500", "bg-purple-500"];
                    const color = colors[i % colors.length];
                    return (
                      <div
                        key={act.id || i}
                        style={{ width: `${act.pourcentageCapital || 0}%` }}
                        className={`${color} h-full transition-all duration-300`}
                        title={`${act.prenom || ""} ${act.nom} : ${act.pourcentageCapital}%`}
                      />
                    );
                  })}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-xs">
                    <thead>
                      <tr className="text-subtle font-semibold uppercase tracking-wider text-left">
                        <th className="py-3 px-4">Associé / Actionnaire</th>
                        <th className="py-3 px-4">Qualité</th>
                        <th className="py-3 px-4">% Capital</th>
                        <th className="py-3 px-4">% Droits de Vote</th>
                        <th className="py-3 px-4">Valeur des Titres</th>
                        <th className="py-3 px-4">Nature de Détention</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {actionnaires.map((act, i) => (
                        <tr key={act.id || i} className="hover:bg-surface-muted/40 transition-colors">
                          <td className="py-3 px-4 font-bold text-primary flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-surface-muted border border-workspace flex items-center justify-center font-black text-[10px]">
                              {act.nom?.charAt(0) || "A"}
                            </span>
                            {act.prenom} {act.nom}
                          </td>
                          <td className="py-3 px-4 text-subtle">
                            {act.type === "ORGANIZATION" ? "Personne Morale (Holding)" : "Personne Physique"}
                          </td>
                          <td className="py-3 px-4 font-bold text-secondary">
                            {act.pourcentageCapital}%
                          </td>
                          <td className="py-3 px-4 font-semibold text-primary">
                            {act.pourcentageDroitsVote}%
                          </td>
                          <td className="py-3 px-4 font-mono font-medium text-primary">
                            {formatEuro(act.valeurEstimee || (societe.valorisationEstimee * (act.pourcentageCapital / 100)))}
                          </td>
                          <td className="py-3 px-4 text-subtle">
                            {act.natureDetention || "Pleine Propriété"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* REGISTRE DES BÉNÉFICIAIRES EFFECTIFS (RBE) */}
              <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                <div className="flex items-start justify-between gap-4 border-b border-workspace pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-primary">Registre des Bénéficiaires Effectifs (RBE)</h3>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200">
                          Conformité LCB-FT
                        </span>
                      </div>
                      <p className="text-xs text-subtle mt-0.5">
                        Personnes physiques exerçant un contrôle direct ou indirect (&gt; 25% du capital ou des droits de vote)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {beneficiairesEffectifs.map((bo, i) => (
                    <div key={bo.id || i} className="p-4 rounded-xl border border-workspace bg-surface-muted/20 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span className="font-bold text-sm text-primary">{bo.prenom} {bo.nom}</span>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-black text-secondary bg-secondary/10 border border-secondary/20">
                          {bo.pourcentageCapital}% Détenu
                        </span>
                      </div>
                      <p className="text-xs text-subtle">
                        <strong>Modalité de contrôle :</strong> {bo.modaliteControle || "Détention directe du capital social"}
                      </p>
                      <div className="flex items-center justify-between text-[11px] text-subtle pt-2 border-t border-workspace/60">
                        <span>Droits de vote : <strong>{bo.pourcentageDroitsVote}%</strong></span>
                        <span>Déclaration greffe : {bo.dateDeclaration ? new Date(bo.dateDeclaration).toLocaleDateString("fr-FR") : "Vérifiée"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 4 : SANTÉ FINANCIÈRE & RATIOS                         */}
          {/* ========================================================= */}
          {activeTab === "financier" && (
            <div className="space-y-6">
              {/* Bilan & Soldes Intermédiaires de Gestion */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                  <div className="flex items-center gap-3 border-b border-workspace pb-3">
                    <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-primary">Compte de Résultat & Rentabilité</h3>
                      <p className="text-xs text-subtle">Activité et marges opérationnelles</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <AuditField label="Chiffre d'Affaires" value={formatEuro(societe.chiffreAffaires)} />
                    <AuditField label="EBITDA / EBE" value={formatEuro(societe.ebitda)} />
                    <AuditField label="Marge d'EBITDA" value={`${margeEbitda} %`} />
                    <AuditField label="Résultat Net" value={formatEuro(societe.resultatNet)} />
                    <AuditField label="Marge Nette" value={`${margeNette} %`} />
                    <AuditField label="Valorisation d'Entreprise" value={formatEuro(societe.valorisationEstimee)} />
                  </div>
                </div>

                <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                  <div className="flex items-center gap-3 border-b border-workspace pb-3">
                    <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
                      <Wallet className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-primary">Trésorerie & Endettement</h3>
                      <p className="text-xs text-subtle">Structure du bilan et solvabilité financière</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <AuditField label="Trésorerie Disponible" value={formatEuro(societe.tresorerieDisponible)} />
                    <AuditField label="Dettes Financières Brutes" value={formatEuro(societe.dettes)} />
                    <AuditField label="Trésorerie Nette" value={formatEuro(tresorerieNette)} />
                    <AuditField label="Levier de Dette (Dette / EBITDA)" value={`${levierDette} x`} />
                    <AuditField label="Besoin de Financement" value={formatEuro(societe.besoinFinancement || 0)} />
                    <AuditField label="Placements de Trésorerie" value={formatEuro(societe.placementsEntreprise || 0)} />
                  </div>
                </div>
              </div>

              {/* Ratios & Recommandations de gestion */}
              <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm">
                <h3 className="text-base font-bold text-primary mb-2">Diagnostic de Solvabilité</h3>
                <p className="text-xs text-subtle mb-4">
                  Analyse des équilibres financiers pour optimisation fiscale et patrimoniale
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                  <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-primary">Capacité de Remboursement</span>
                      <span className="text-emerald-600 font-black">Optimale (&lt; 3 ans)</span>
                    </div>
                    <p className="text-subtle text-[11px]">
                      La structure génère une capacité d'autofinancement suffisante pour amortir ses dettes bancaires en moins de 2 ans.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-primary">Trésorerie Excédentaire</span>
                      <span className="text-secondary font-black">À arbitrer</span>
                    </div>
                    <p className="text-subtle text-[11px]">
                      {formatEuro(societe.tresorerieDisponible)} dorment sur les comptes courants. Une allocation de trésorerie d'entreprise (contrat de capi) est recommandée.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-primary">Protection Homme-Clé</span>
                      <span className="text-amber-600 font-black">À souscrire</span>
                    </div>
                    <p className="text-subtle text-[11px]">
                      En cas de défaillance du dirigeant, l'impact sur l'EBITDA serait majeur. Couverture prévoyance croisée recommandée.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 5 : CARTOGRAPHIE & ORGANIGRAMME                       */}
          {/* ========================================================= */}
          {activeTab === "organigramme" && (
            <div className="space-y-6">
              {/* CARTOGRAPHIE MULTI-NIVEAUX */}
              <div className="workspace-card p-6 sm:p-8 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-8">
                <div>
                  <h3 className="text-lg font-bold text-primary">Cartographie du Groupe & Participations</h3>
                  <p className="text-xs text-subtle mt-0.5">
                    Visualisation des actionnaires (amont), de la société pivot et de ses filiales (aval)
                  </p>
                </div>

                <div className="flex flex-col items-center space-y-6">
                  
                  {/* NIVEAU 1 : ACTIONNAIRES / DÉTENTION EN AMONT */}
                  <div className="w-full">
                    <span className="text-[11px] font-bold text-subtle uppercase tracking-wider block text-center mb-3">
                      Actionnariat & Contrôle en Amont
                    </span>
                    <div className="flex justify-center gap-4 flex-wrap">
                      {actionnaires.map((act, i) => (
                        <div
                          key={act.id || i}
                          className="bg-surface border border-workspace shadow-hp-xs rounded-xl p-4 w-64 flex flex-col items-center text-center"
                        >
                          <div className="w-10 h-10 rounded-full bg-surface-muted flex items-center justify-center text-primary font-bold text-xs mb-2">
                            {act.nom?.charAt(0) || "A"}
                          </div>
                          <h4 className="text-xs font-bold text-primary truncate w-full">{act.prenom} {act.nom}</h4>
                          <span className="text-[10px] text-subtle">{act.type === "ORGANIZATION" ? "Holding" : "Personne Physique"}</span>
                          <span className="mt-2 text-xs font-black text-secondary bg-secondary/10 px-2.5 py-0.5 rounded-full border border-secondary/20">
                            {act.pourcentageCapital}% du capital
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* FLÈCHE DE LIAISON VERS LE CENTRE */}
                  <div className="flex flex-col items-center text-secondary">
                    <div className="w-0.5 h-6 bg-secondary/40" />
                    <span className="text-[10px] font-bold bg-secondary/15 px-2 py-0.5 rounded-full border border-secondary/20 my-1">
                      Détient à 100%
                    </span>
                    <div className="w-0.5 h-6 bg-secondary/40" />
                  </div>

                  {/* NIVEAU 2 : LA SOCIÉTÉ AU CENTRE */}
                  <div className="p-6 rounded-2xl bg-secondary/10 border-2 border-secondary shadow-hp-md max-w-md w-full text-center space-y-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-secondary uppercase tracking-wider">
                      <Building2 className="w-3.5 h-3.5" /> Société Pivot
                    </span>
                    <h2 className="text-xl font-black text-primary">
                      {societe.denomination || societe.raisonSociale}
                    </h2>
                    <p className="text-xs text-subtle font-mono">
                      SIREN : {formatSiren(societe.siren)} • {societe.formeJuridique}
                    </p>
                    <div className="pt-2 flex justify-center gap-3 text-xs">
                      <span className="font-bold text-primary">CA : {formatEuroCompact(societe.chiffreAffaires)}</span>
                      <span>•</span>
                      <span className="font-bold text-secondary">Valo : {formatEuroCompact(societe.valorisationEstimee)}</span>
                    </div>
                  </div>

                  {/* NIVEAU 3 : FILIALES ET PARTICIPATIONS DÉTENUES EN AVAL */}
                  {filiales.length > 0 ? (
                    <div className="w-full">
                      <div className="flex flex-col items-center text-secondary mb-3">
                        <div className="w-0.5 h-6 bg-secondary/40" />
                        <span className="text-[10px] font-bold bg-secondary/15 px-2 py-0.5 rounded-full border border-secondary/20 my-1">
                          Contrôle & Filiales
                        </span>
                        <div className="w-0.5 h-6 bg-secondary/40" />
                      </div>

                      <div className="flex justify-center gap-4 flex-wrap">
                        {filiales.map((fil, i) => (
                          <div
                            key={fil.id || i}
                            onClick={() => navigate(`/prospects/societes/${fil.id}`)}
                            className="bg-surface border border-workspace shadow-hp-xs hover:shadow-hp-md rounded-xl p-4 w-64 flex flex-col items-center text-center cursor-pointer hover:border-secondary transition-all"
                          >
                            <Building2 className="w-8 h-8 text-secondary mb-2" />
                            <h4 className="text-xs font-bold text-primary truncate w-full">{fil.denomination}</h4>
                            <span className="text-[10px] text-subtle">{fil.formeJuridique}</span>
                            <span className="mt-2 text-xs font-black text-emerald-600 dark:bg-emerald-900/30 px-2.5 py-0.5 rounded-full border border-emerald-200">
                              Détenue à {fil.pourcentageDetenu}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="pt-4 text-center text-xs text-subtle">
                      Aucune filiale détenue enregistrée pour cette entité.
                    </div>
                  )}

                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 6 : AUDIT & STRATÉGIE (SWOT & PROJETS)                */}
          {/* ========================================================= */}
          {activeTab === "audit" && (
            <div className="space-y-6">
              {/* MATRICE SWOT */}
              <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                <div className="flex items-center gap-3 border-b border-workspace pb-3">
                  <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-primary">Matrice Stratégique SWOT</h3>
                    <p className="text-xs text-subtle">Forces, faiblesses, opportunités et facteurs de risque</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  {/* Forces */}
                  <div className="p-4 rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10 space-y-2">
                    <span className="font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                      Forces & Atouts
                    </span>
                    <p className="text-emerald-950 dark:text-emerald-100 font-medium">
                      {societe.forces || "Non renseigné"}
                    </p>
                  </div>

                  {/* Faiblesses */}
                  <div className="p-4 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10 space-y-2">
                    <span className="font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                      Faiblesses Internes
                    </span>
                    <p className="text-amber-950 dark:text-amber-100 font-medium">
                      {societe.faiblesses || "Non renseigné"}
                    </p>
                  </div>

                  {/* Opportunités */}
                  <div className="p-4 rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50/50 dark:bg-blue-900/10 space-y-2">
                    <span className="font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
                      Opportunités Patrimoniales
                    </span>
                    <p className="text-blue-950 dark:text-blue-100 font-medium">
                      {societe.opportunites || "Non renseigné"}
                    </p>
                  </div>

                  {/* Risques */}
                  <div className="p-4 rounded-xl border border-rose-200 dark:border-rose-800/50 bg-rose-50/50 dark:bg-rose-900/10 space-y-2">
                    <span className="font-bold text-rose-800 dark:text-rose-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                      <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
                      Risques Juridiques & Fiscaux
                    </span>
                    <p className="text-rose-950 dark:text-rose-100 font-medium">
                      {societe.risques || "Non renseigné"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Projets Stratégiques & Préconisations */}
              <div className="workspace-card p-6 rounded-2xl border border-workspace bg-surface shadow-hp-sm space-y-4">
                <div className="flex items-center gap-3 border-b border-workspace pb-3">
                  <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
                    <Award className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-primary">Projets d'Entreprise & Préconisations CGP</h3>
                    <p className="text-xs text-subtle">Feuille de route et plan d'action d'ingénierie patrimoniale</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <AuditField label="Projet de Cession" value={societe.projetCession === true ? `Oui${societe.horizonCession ? ` (Horizon ${societe.horizonCession})` : ""}` : (societe.projetCession === false ? "Non" : "Non renseigné")} />
                  <AuditField label="Transmission Familiale (Dutreil)" value={societe.transmissionEntreprise === true ? "Envisagée" : (societe.transmissionEntreprise === false ? "Non envisagée" : "Non renseigné")} />
                  <AuditField label="Recherche de Financement" value={societe.leveeFonds === true ? `Levée de fonds${societe.besoinFinancement ? ` (${formatEuro(societe.besoinFinancement)})` : ""}` : (societe.leveeFonds === false ? "Autofinancée" : "Non renseigné")} />
                  <AuditField label="Projet d'Acquisition (Croissance)" value={societe.acquisition === true ? "Recherche active de cibles" : (societe.acquisition === false ? "Non" : "Non renseigné")} />
                </div>

                <div className="p-4 rounded-xl bg-surface-muted/30 border border-workspace space-y-2 mt-4">
                  <span className="text-xs font-bold text-primary block">
                    Synthèse des Préconisations du Conseiller :
                  </span>
                  <p className="text-xs text-subtle leading-relaxed">
                    {societe.recommandations || "Aucune préconisation enregistrée pour cette entité."}
                  </p>
                </div>
              </div>
            </div>
          )}

            </motion.div>
          )}
        </AnimatePresence>

        {/* MODALE D'ÉDITION ET D'AUDIT COMPLET */}
        {showAuditModal && (
          <SocieteAudit
            societe={societe}
            open={showAuditModal}
            onClose={() => setShowAuditModal(false)}
            onSave={fetchSociete}
          />
        )}

      </div>
    </PageAnimator>
  );
}
